import React from 'react';
import './GeneratedInsight.scss';
import { useTranslation } from 'react-i18next';
import type { InsightsOverviewResponse } from '@brand24-original/llm-types';
import { percentOf, resolveOverviewInsightVariant } from '../../helpers/overviewInsightHelpers';

interface GeneratedInsightProps {
  overview: InsightsOverviewResponse | null;
  isLoading: boolean;
  /** Display name of the analyzed brand, interpolated into the generated sentence. */
  brandLabel: string;
  /** Own brand → the CTA is the project website; a competitor → a domain on the Keywords page. */
  isOwnBrand: boolean;
}

const GeneratedInsight: React.FC<GeneratedInsightProps> = ({
  overview,
  isLoading,
  brandLabel,
  isOwnBrand,
}) => {
  const { t } = useTranslation();

  if (isLoading || !overview || overview.totalResponses === 0) {
    return null;
  }

  const { totalResponses, visibleResponses, citedResponses, visibleAndCitedResponses } = overview;

  // Bez danych o domenach marki nie ma czego interpretować — CTA zamiast wniosku.
  if (citedResponses === null || visibleAndCitedResponses === null) {
    return (
      <div className="stats__panel generated-insight generated-insight--cta">
        <span className="generated-insight__text">
          {t(isOwnBrand ? 'pageTrafficNoDomainDescription' : 'insightPage.overview.noDomainForBrand', {
            brand: brandLabel,
          })}
        </span>
      </div>
    );
  }

  const visibilityPct = percentOf(visibleResponses, totalResponses);
  const citedPct = percentOf(citedResponses, totalResponses);
  // Own-source coverage: jaki % widoczności marki jest poparty jej własnym źródłem.
  const coveragePct = percentOf(visibleAndCitedResponses, visibleResponses);
  const variant = resolveOverviewInsightVariant(visibilityPct, citedPct, coveragePct);
  // A handful of brand mentions makes coverage noise — the marginal variant stands alone.
  const showCoverage = visibleResponses > 0 && variant !== 'marginalPresence';

  return (
    <div className="stats__panel generated-insight">
      <span className="generated-insight__text">
        {t(`insightPage.overview.insight.${variant}`, { brand: brandLabel })}{' '}
        {showCoverage && t('insightPage.overview.insight.coverage', { coverage: coveragePct })}
      </span>
    </div>
  );
};

export default GeneratedInsight;
